import { ensureBrandFontCoverage } from '@src/services/branding/ensureBrandFontCoverage';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  CAPTIONS_LANGUAGE_CHANGED_EVENT,
  isOriginalLang,
} from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

import {
  notifyCaptionFontSubstitution,
  switchCaptionLanguage,
} from './switchCaptionLanguage';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';

const logger = LogManager.getLogger('feature:captions-language-applier');

export interface UseCaptionsLanguageApplierOptions {
  /**
   * On `applied`, force the subtitles layer visible. Right-side captions
   * panel toggle uses this so the user immediately sees the new caption
   * track. Left transcript picker leaves subtitle visibility under the
   * user's control. Default: false.
   */
  showSubtitlesOnApply?: boolean;

  /**
   * On `applied`, dispatch `CAPTIONS_LANGUAGE_CHANGED_EVENT` on `window`
   * so listeners on other surfaces (e.g., LeftSidePanel) can sync their
   * own URL or in-memory state. Set this true on call-sites that are NOT
   * already updating the URL themselves. Default: false.
   */
  dispatchGlobalEvent?: boolean;
}

/**
 * Shared caption-language applier used by Creator Studio's left transcript
 * picker (`LeftSidePanel`) and the right captions panel toggle
 * (`CaptionsLanguageHeaderToggle`). Centralises the
 * `applyCaptionsLanguage` -> result-switch -> on-applied side-effect
 * dance so the two surfaces stay in lockstep.
 *
 * PLAT-9990: previously each surface owned its own copy of the switch
 * block, and the left transcript surface short-circuited under PLAT-9621
 * leaving the editor's karaoke config (and therefore the composition
 * translation projection) untouched. This hook is the single point of
 * truth — any future caller (e.g., the composition card quick-edit
 * popover) gets the same post-applied effects automatically.
 *
 * On `applied`:
 *  - Refreshes the editor's karaoke layer (`editorStore.refreshKaraoke`).
 *  - Re-fires the debounced setSource so the PLAT-9966 composition
 *    translation projection picks up the new active language.
 *  - Optionally forces subtitles visible (see {@link
 *    UseCaptionsLanguageApplierOptions.showSubtitlesOnApply}).
 *  - Optionally dispatches the global captions-language-changed event.
 *
 * Returns a stable callback. Errors at `produce-failed` and `exception`
 * are logged via `LogManager`; the caller does not need to handle them.
 *
 * Note: this hook does NOT update the URL. Each call-site owns its own
 * URL semantics (see LeftSidePanel's `setUrlLanguage` and
 * `CaptionsLanguageHeaderToggle`'s `setUrlLang`).
 *
 * PLAT-10816 — `createUndoRedoEntry` is per CALL, not a hook option:
 * `CaptionsLanguageHeaderToggle` shares one applier between a user pick (`true`)
 * and its mount effect / removal fallback (`false`).
 */
export function useCaptionsLanguageApplier(
  editor: IVideoEditor | null,
  options: UseCaptionsLanguageApplierOptions = {},
): (
  lang: string | null,
  callOptions?: { createUndoRedoEntry?: boolean },
) => void {
  const videoCreator = useVideoCreatorStore();
  const { showSubtitlesOnApply = false, dispatchGlobalEvent = false } = options;

  return useCallback(
    (lang: string | null, callOptions?: { createUndoRedoEntry?: boolean }) => {
      if (!editor) return;
      // PLAT-10635: seed default captions from the showcase's default caption
      // template instead of the built-in "Impact" default — the one rule
      // every surface's first "on" applies (PLAT-11877).
      const fallbackTemplate =
        videoCreator.textBrandingService.getDefaultCaptionTemplateConfig(
          videoCreator.getCurrentAspectRatio(),
        );
      // PLAT-10788 (DS6) — load the cached per-language variant when one exists;
      // produce only on a miss.
      const result = switchCaptionLanguage({
        editor,
        karaokeStore: videoCreator.rootStore.karaokeStore,
        lang,
        transcriptLanguage:
          videoCreator.rootStore.transcriptionStore.rawLanguage,
        originalVideoLanguage: videoCreator.story?.originalVideoLanguage,
        fallbackTemplate,
        createUndoRedoEntry: callOptions?.createUndoRedoEntry,
        // PLAT-11416 — the org's curated per-language variant of the active
        // template family, when one exists for the current aspect ratio.
        resolveLanguageVariant: (parentTemplateId, languageKey) =>
          videoCreator.textBrandingService.getKaraokeLanguageVariantConfig(
            parentTemplateId,
            languageKey,
            videoCreator.getCurrentAspectRatio(),
          ),
      });
      switch (result.kind) {
        case 'noop':
        case 'no-config':
        case 'no-track':
          return;
        case 'produce-failed':
          logger.error('commandProduceKaraoke returned false', {
            currentLang: result.currentLang,
            nextLang: result.nextLang,
          });
          return;
        case 'exception':
          logger.error('Failed to re-produce karaoke for language change', {
            currentLang: result.currentLang,
            nextLang: result.nextLang,
            ...extractErrorInfo(result.error),
          });
          return;
        case 'applied':
          videoCreator.rootStore.editorStore?.refreshKaraoke();
          // PLAT-11416 — re-filter the template picker for the new language
          // (language variants only list under their own language).
          videoCreator.textBrandingService.refreshKaraokeTemplatesForLanguage();
          if (showSubtitlesOnApply) {
            videoCreator.setShowSubtitles(true);
          }
          videoCreator.rootStore.videoSourceStore.refreshSource();
          // PLAT-11467 — brand fonts uploaded before PLAT-11412 carry no
          // script coverage, which leaves the translated-graphics font
          // correction with nothing to act on. Repair it here, where a
          // non-Latin language has just been applied and the coverage is
          // about to matter. Fire-and-forget: the switch never waits on font
          // downloads, and the source re-fires when the probe lands.
          void ensureBrandFontCoverage({
            organization: videoCreator.organization,
            targetLanguage: result.nextLang,
            persist: org =>
              videoCreator.datoClientStore.albumRepository.update(org as never),
            onCoverageChanged: () =>
              videoCreator.rootStore.videoSourceStore.refreshSource(),
          });
          // PLAT-11409 (DE1) — passive notice when the caption font was
          // auto-swapped/restored for the language's script.
          notifyCaptionFontSubstitution(
            toast => videoCreator.showToast(toast),
            result,
          );
          if (dispatchGlobalEvent) {
            window.dispatchEvent(
              new CustomEvent(CAPTIONS_LANGUAGE_CHANGED_EVENT, {
                detail: {
                  language: isOriginalLang(result.nextLang)
                    ? null
                    : result.nextLang,
                },
              }),
            );
          }
          return;
      }
    },
    [editor, videoCreator, showSubtitlesOnApply, dispatchGlobalEvent],
  );
}
